import { IOfferingRes, IOfferingSummaryResSchema } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useGetListData } from '../../../api/queryUtils';
import {
  MHStack,
  MPageContainer,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { getOfferingEditRoute } from '../../../constants/routes';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import PageHeader from '../components/PageHeader';
import TableHeader from '../components/TableHeader';
import { OfferingActions } from './components/OfferingActions';
import {
  OFFERING_LIST_CATALOG,
  OfferingListColumnContext,
} from './offeringList.columns';

const OfferingListPage: FC = () => {
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.OFFERING_LIST),
  );

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.OFFERING_LIST),
  );

  const [searchTerm, setSearchTerm] = useAtom(
    tableSearchTermSelector(FilterStateKeys.OFFERING_LIST),
  );

  // this is temporary until BE refactor to show archived
  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('name');

  const filterParams = useMemo(() => {
    const params = buildFilterParamsRequestObject(
      transformTableFilterValue(filters),
      searchTerm,
      searchKey,
    );
    return params;
  }, [filters, searchTerm, searchKey]);

  const tableRef = useRef<DataTableRef>(null);

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const {
    data: offeringList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
  } = useGetListData<IOfferingSummaryResSchema>('productCatalogOfferings', {
    config: pager,
    filters: {
      ...filterParams,
    },
  });

  const loading = isLoading || (isRefetching && !isFetched);
  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: offeringList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    setFilters(getDefaultFilter(FilterStateKeys.OFFERING_LIST));
    setPager(getDefaultPager(FilterStateKeys.OFFERING_LIST));
    setSearchTerm('');
    tableRef.current?.resetFilters();
  };

  const { canCreateSettings } = useSettingsReadOnlyRule();

  const actionBodyTemplate = useCallback(
    (rowData: IOfferingSummaryResSchema) => (
      <OfferingActions offering={rowData as IOfferingRes} source="LIST" />
    ),
    [],
  );

  const searchComponentReset = React.useRef<any>(null);

  const columnContext = useMemo<OfferingListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    OFFERING_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer enableAccountSearch>
      <PageHeader />

      <TableHeader
        model="Offering"
        showCreateNewBtn={!isTrulyEmptyList && canCreateSettings}
      >
        {!isTrulyEmptyList && (
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={setSearchTerm}
              count={offeringList?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="offerings"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        )}
      </TableHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={offeringList?.content ?? []}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={offeringList?.totalElements}
        totalPages={offeringList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(e, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getOfferingEditRoute(e.id),
            openInNewWindow,
          )
        }
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no offerings here.',
          smallMessage: null,
          btnLabel: canCreateSettings ? 'New Offering' : '',
          to: ROUTES.OFFERING_NEW,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />
    </MPageContainer>
  );
};

export default OfferingListPage;
